import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { MediaViewer, VideoCard } from '../components/MediaViewer';
import { cn } from '../lib/utils';
import { getProductDisplayName } from '../lib/helpers';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Video, 
  Loader2,
  ExternalLink,
  Download
} from 'lucide-react';

export default function UGCAdDetail() {
  const { ecomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [ugcAd, setUgcAd] = useState(null);
  const [fullVideos, setFullVideos] = useState(null);
  const [shorts, setShorts] = useState(null);
  const [winningDetail, setWinningDetail] = useState(null);
  
  // Media viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState({ url: '', type: 'video', title: '' });
  const [allMedia, setAllMedia] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [productRes, ugcRes, fullVideosRes, shortsRes, winningRes] = await Promise.all([
        supabase.from('products').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_ads').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_full_videos').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('ugc_shorts').select('*').eq('ecom_id', ecomId).single(),
        supabase.from('winning_product_details').select('ecom_id, product_name').eq('ecom_id', ecomId).single(),
      ]);

      setProduct(productRes.data);
      setUgcAd(ugcRes.data);
      setFullVideos(fullVideosRes.data);
      setShorts(shortsRes.data);
      setWinningDetail(winningRes.data);
    } catch (error) {
      console.error('Error fetching UGC ad:', error);
      toast.error('Failed to load UGC ad details');
    } finally {
      setLoading(false);
    }
  }, [ecomId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const displayName = getProductDisplayName(ecomId, winningDetail ? [winningDetail] : []);

  const openViewer = (url, title, mediaList = [], index = 0) => {
    setCurrentMedia({ url, type: 'video', title });
    setAllMedia(mediaList);
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const handleNavigate = (newIndex) => {
    if (allMedia[newIndex]) {
      setCurrentIndex(newIndex);
      setCurrentMedia({
        url: allMedia[newIndex].url,
        type: 'video',
        title: allMedia[newIndex].title
      });
    }
  };

  // Collect all videos for gallery navigation
  const getAllFullVideos = () => {
    const videos = [];
    for (let i = 1; i <= 4; i++) {
      if (fullVideos?.[`video${i}`]) {
        videos.push({ url: fullVideos[`video${i}`], title: `Full Video ${i}` });
      }
    }
    return videos;
  };

  const getAllShorts = () => {
    const videos = [];
    for (let i = 1; i <= 4; i++) {
      if (shorts?.[`video${i}`]) {
        videos.push({ url: shorts[`video${i}`], title: `Short ${i}` });
      }
    }
    return videos;
  };

  const handleDownloadAll = async (videos, prefix) => {
    for (let i = 0; i < videos.length; i++) {
      try {
        const response = await fetch(videos[i].url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${prefix}_${i + 1}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Failed to download ${prefix} ${i + 1}`);
      }
    }
    toast.success(`Downloaded ${videos.length} videos`);
  };

  if (loading) {
    return (
      <Layout productName="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!ugcAd) {
    return (
      <Layout>
        <div className="text-center py-16">
          <Video className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">UGC Ad not found</p>
          <button
            onClick={() => navigate('/ads')}
            className="px-6 py-2 bg-cyan-500 text-white rounded-lg"
          >
            Back to Ads
          </button>
        </div>
      </Layout>
    );
  }

  const fullVideosList = getAllFullVideos();
  const shortsList = getAllShorts();

  return (
    <Layout productName={displayName}>
      <div data-testid="ugc-ad-detail">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ads')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Video className="w-6 h-6 text-violet-600" />
                </div>
                
                <div>
                  <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">
                    {displayName}
                  </h1>
                  <div className="flex items-center gap-3 mt-1">
                    <StatusBadge status={ugcAd.status} lane="ugc" />
                    <span className="text-sm text-slate-500 capitalize">{product?.platform}</span>
                  </div>
                </div>
              </div>
            </div>

            {product?.url && (
              <a
                href={product.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                View Product
              </a>
            )}
          </div>
        </div>

        {/* Full Videos Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-[#1E1B4B]">Full Videos</h2>
              <StatusBadge status={ugcAd.full_video_status || fullVideos?.status} lane="ugc" />
            </div>
            {fullVideosList.length > 0 && (
              <button
                onClick={() => handleDownloadAll(fullVideosList, 'full_video')}
                className="px-4 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Download All ({fullVideosList.length})
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => {
              const videoUrl = fullVideos?.[`video${i}`];
              const videoIndex = fullVideosList.findIndex(v => v.url === videoUrl);
              return (
                <VideoCard
                  key={i}
                  url={videoUrl}
                  label={`Video ${i}`}
                  onView={() => videoUrl && openViewer(videoUrl, `Full Video ${i}`, fullVideosList, videoIndex >= 0 ? videoIndex : 0)}
                />
              );
            })}
          </div>
        </div>

        {/* Shorts Section */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-[#1E1B4B]">Short Videos</h2>
              <StatusBadge status={ugcAd.shorts_status || shorts?.status} lane="ugc" />
            </div>
            {shortsList.length > 0 && (
              <button
                onClick={() => handleDownloadAll(shortsList, 'short')}
                className="px-4 py-2 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <Download className="w-4 h-4" />
                Download All ({shortsList.length})
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => {
              const videoUrl = shorts?.[`video${i}`];
              const videoIndex = shortsList.findIndex(v => v.url === videoUrl);
              return (
                <VideoCard
                  key={i}
                  url={videoUrl}
                  label={`Short ${i}`}
                  onView={() => videoUrl && openViewer(videoUrl, `Short ${i}`, shortsList, videoIndex >= 0 ? videoIndex : 0)}
                />
              );
            })}
          </div>
        </div>

        {/* Media Viewer */}
        <MediaViewer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          mediaUrl={currentMedia.url}
          mediaType={currentMedia.type}
          title={currentMedia.title}
          allMedia={allMedia}
          currentIndex={currentIndex}
          onNavigate={handleNavigate}
        />
      </div>
    </Layout>
  );
}
